import { useRef, useState } from 'react';
import type { PdfSignatureMethod } from '@canvas/shared-types';
import { usePdfStore } from '@/stores/pdfStore';
import { createSignature } from '@/lib/pdf/signatureStore';
import DrawSignaturePad, {
  type DrawSignaturePadHandle,
} from './signature/DrawSignaturePad';
import WriteSignaturePad, {
  type WriteSignaturePadHandle,
} from './signature/WriteSignaturePad';
import ImageSignaturePad, {
  type ImageSignaturePadHandle,
} from './signature/ImageSignaturePad';

type MethodTab = PdfSignatureMethod;

const TABS: { id: MethodTab; label: string; icon: string }[] = [
  { id: 'draw', label: 'Draw signature', icon: '✎' },
  { id: 'write', label: 'Write signature', icon: 'I' },
  { id: 'image', label: 'Use image', icon: '▣' },
];

export default function CreateSignatureModal() {
  const open = usePdfStore((s) => s.signatureModalOpen);
  const setOpen = usePdfStore((s) => s.setSignatureModalOpen);
  const addSignature = usePdfStore((s) => s.addSignature);

  const [tab, setTab] = useState<MethodTab>('draw');
  const [color, setColor] = useState('#000000');
  const [thicknessPct, setThicknessPct] = useState(25);
  const [canCreate, setCanCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const drawRef = useRef<DrawSignaturePadHandle>(null);
  const writeRef = useRef<WriteSignaturePadHandle>(null);
  const imageRef = useRef<ImageSignaturePadHandle>(null);

  if (!open) return null;

  const close = () => {
    if (saving) return;
    setOpen(false);
    setCanCreate(false);
    setLocalError(null);
    setTab('draw');
  };

  const handleCreate = async () => {
    setLocalError(null);

    if (!canCreate) {
      setLocalError(
        tab === 'write'
          ? 'Type your signature first'
          : tab === 'image'
            ? 'Choose an image first'
            : 'Draw your signature first'
      );
      return;
    }

    setSaving(true);
    try {
      let blob: Blob;
      let name = 'Signature';

      if (tab === 'draw') {
        const pad = drawRef.current;
        if (!pad || pad.isEmpty()) {
          throw new Error('Draw your signature first');
        }
        blob = await pad.toPngBlob();
      } else if (tab === 'write') {
        const pad = writeRef.current;
        if (!pad || pad.isEmpty()) {
          throw new Error('Type your signature first');
        }
        blob = await pad.toPngBlob();
        name = pad.getName();
      } else {
        const pad = imageRef.current;
        if (!pad || pad.isEmpty()) {
          throw new Error('Choose an image first');
        }
        blob = await pad.toPngBlob();
        name = 'Image signature';
      }

      if (!blob || blob.size < 32) {
        throw new Error('Signature image is empty — try again');
      }

      const sig = await createSignature({ pngBlob: blob, name, method: tab });
      addSignature(sig);
      setOpen(false);
      setCanCreate(false);
      setLocalError(null);
      setTab('draw');
    } catch (err) {
      console.error('Create signature failed', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to create signature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pdfe-modal-overlay" role="presentation" onClick={close}>
      <div
        className="pdfe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdfe-create-sig-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pdfe-modal-head">
          <h2 id="pdfe-create-sig-title">Create signature</h2>
          <button
            type="button"
            className="pdfe-modal-close"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="pdfe-sig-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`pdfe-sig-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => {
                setTab(t.id);
                setCanCreate(false);
                setLocalError(null);
              }}
            >
              <span aria-hidden>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'draw' && (
          <>
            <div className="pdfe-sig-draw-toolbar">
              <label className="pdfe-sig-color">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  aria-label="Ink color"
                />
                <span>{color.toUpperCase()}</span>
              </label>
              <label className="pdfe-sig-thickness">
                <span className="pdfe-sig-thickness-icon" aria-hidden>
                  ≡
                </span>
                <input
                  type="range"
                  min={5}
                  max={100}
                  value={thicknessPct}
                  onChange={(e) => setThicknessPct(Number(e.target.value))}
                  aria-label="Stroke thickness"
                />
                <em>{thicknessPct}%</em>
              </label>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  drawRef.current?.clear();
                  setCanCreate(false);
                }}
              >
                Clear
              </button>
            </div>
            <div className="pdfe-sig-pad-wrap">
              <DrawSignaturePad
                ref={drawRef}
                color={color}
                thicknessPct={thicknessPct}
                onDirtyChange={setCanCreate}
              />
            </div>
          </>
        )}

        {tab === 'write' && (
          <div className="pdfe-sig-pad-wrap">
            <div className="pdfe-sig-draw-toolbar">
              <label className="pdfe-sig-color">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  aria-label="Text color"
                />
                <span>{color.toUpperCase()}</span>
              </label>
            </div>
            <WriteSignaturePad
              ref={writeRef}
              color={color}
              onDirtyChange={setCanCreate}
            />
          </div>
        )}

        {tab === 'image' && (
          <div className="pdfe-sig-pad-wrap">
            <ImageSignaturePad ref={imageRef} onDirtyChange={setCanCreate} />
          </div>
        )}

        {localError && (
          <div className="pdfe-sig-modal-error" role="alert">
            {localError}
          </div>
        )}

        <footer className="pdfe-modal-foot">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => void handleCreate()}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </footer>
      </div>
    </div>
  );
}
